import { useMemo } from 'react';
import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiClient, ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import { DEFAULT_GRADEBOOK_BATCH_SIZE, createGradebookBatchRanges } from '@/features/gradebook/gradebookService';
import { hashUserId } from '@/lib/hash/userIdHash';

import type { ResolvedExtensionMember } from './types';

interface RawKuduUserProfile {
  first_name?: string | null;
  firstName?: string | null;
  last_name?: string | null;
  lastName?: string | null;
  preferred_email?: string | null;
  preferredEmail?: string | null;
}

interface RawCourseStudent {
  id?: number | string | null;
  profile?: RawKuduUserProfile | null;
  uid?: number | string | null;
}

interface RawCourseStudentGroup {
  course_student_ids?: Array<number | string> | null;
  course_student_group?: RawCourseStudentGroup | null;
  course_students?: RawCourseStudent[] | null;
  courseStudentIds?: Array<number | string> | null;
  courseStudents?: RawCourseStudent[] | null;
}

interface RawGetCourseStudentGroupResponse {
  course_student_group?: RawCourseStudentGroup | null;
  courseStudentGroup?: RawCourseStudentGroup | null;
}

interface RawGetCourseStudentsResponse {
  course_students?: RawCourseStudent[] | null;
  courseStudents?: RawCourseStudent[] | null;
}

export interface RosterHashMapResult {
  hashToProfile: Map<string, ResolvedExtensionMember>;
  profilesById: Map<string, ResolvedExtensionMember>;
  rosterUserIds: string[];
}

interface FetchRosterHashMapParams {
  apiClient: ApiClient;
  batchSize?: number;
  courseId: string;
  instructorUserId: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeIdentifier(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  return normalizeString(value);
}

function unwrapResponse<T>(
  response: T | LegacyKuduResponseEnvelope<T> | null | undefined,
): T | null {
  if (isRecord(response) && 'Response' in response) {
    return unwrapResponse(response.Response as T);
  }

  return isRecord(response) ? (response as T) : null;
}

function toCourseStudentArray(value: unknown): RawCourseStudent[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawCourseStudent[];
}

function toIdentifierArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => normalizeIdentifier(entry))
    .filter((entry): entry is string => Boolean(entry));
}

function getProfileField(
  profile: RawKuduUserProfile | null | undefined,
  snakeKey: keyof RawKuduUserProfile,
  camelKey: keyof RawKuduUserProfile,
) {
  return normalizeString(profile?.[snakeKey]) ?? normalizeString(profile?.[camelKey]);
}

function normalizeRosterStudent(
  rawStudent: RawCourseStudent,
): ResolvedExtensionMember | null {
  const userId =
    normalizeIdentifier(rawStudent.id) ?? normalizeIdentifier(rawStudent.uid);

  if (!userId) {
    return null;
  }

  const profile = isRecord(rawStudent.profile)
    ? (rawStudent.profile as RawKuduUserProfile)
    : null;

  return {
    firstName: getProfileField(profile, 'first_name', 'firstName') ?? '',
    lastName: getProfileField(profile, 'last_name', 'lastName') ?? '',
    preferredEmail:
      getProfileField(profile, 'preferred_email', 'preferredEmail') ?? userId,
    userId,
  };
}

async function fetchRosterGroup(
  apiClient: ApiClient,
  courseId: string,
  instructorUserId: string,
) {
  const response = await apiClient.invokeKuduMethod<
    | RawGetCourseStudentGroupResponse
    | LegacyKuduResponseEnvelope<RawGetCourseStudentGroupResponse>,
    {
      course_id: string;
      include_student_details: boolean;
      is_instructor_request: boolean;
      user_id: string;
    }
  >('GetCourseStudentGroup', {
    course_id: courseId,
    include_student_details: true,
    is_instructor_request: true,
    user_id: instructorUserId,
  });
  const payload = unwrapResponse(response);

  return payload?.course_student_group ?? payload?.courseStudentGroup ?? null;
}

async function fetchRosterStudentBatch(
  apiClient: ApiClient,
  courseId: string,
  startIndex: number,
  endIndex: number,
) {
  const response = await apiClient.invokeKuduMethod<
    | RawGetCourseStudentsResponse
    | LegacyKuduResponseEnvelope<RawGetCourseStudentsResponse>,
    {
      course_id: string;
      end_index: number;
      start_index: number;
    }
  >('GetCourseStudents', {
    course_id: courseId,
    end_index: endIndex,
    start_index: startIndex,
  });
  const payload = unwrapResponse(response);

  return toCourseStudentArray(payload?.course_students ?? payload?.courseStudents);
}

export async function fetchRosterHashMap({
  apiClient,
  batchSize = DEFAULT_GRADEBOOK_BATCH_SIZE,
  courseId,
  instructorUserId,
}: FetchRosterHashMapParams): Promise<RosterHashMapResult> {
  const group = await fetchRosterGroup(apiClient, courseId, instructorUserId);
  const groupStudents = toCourseStudentArray(
    group?.course_students ?? group?.courseStudents,
  );
  const rosterUserIds = Array.from(
    new Set([
      ...toIdentifierArray(group?.course_student_ids ?? group?.courseStudentIds),
      ...groupStudents
        .map((student) =>
          normalizeIdentifier(student.id) ?? normalizeIdentifier(student.uid),
        )
        .filter((userId): userId is string => Boolean(userId)),
    ]),
  );

  const batchRanges = createGradebookBatchRanges(rosterUserIds.length, batchSize);
  const batchStudents = (
    await Promise.all(
      batchRanges.map((range) =>
        fetchRosterStudentBatch(
          apiClient,
          courseId,
          range.startIndex,
          range.endIndex,
        ),
      ),
    )
  ).flat();
  const sourceStudents = batchStudents.length > 0 ? batchStudents : groupStudents;
  const profilesById = new Map<string, ResolvedExtensionMember>();

  sourceStudents.forEach((student) => {
    const profile = normalizeRosterStudent(student);

    if (profile) {
      profilesById.set(profile.userId, profile);
    }
  });

  rosterUserIds.forEach((userId) => {
    if (!profilesById.has(userId)) {
      profilesById.set(userId, {
        firstName: '',
        lastName: '',
        preferredEmail: userId,
        userId,
      });
    }
  });

  const hashToProfile = new Map<string, ResolvedExtensionMember>();
  await Promise.all(
    [...profilesById.values()].map(async (profile) => {
      hashToProfile.set(await hashUserId(profile.userId), profile);
    }),
  );

  return {
    hashToProfile,
    profilesById,
    rosterUserIds: [...profilesById.keys()],
  };
}

export const rosterHashMapQueryKey = (courseId: string) =>
  ['content', 'timeExtensions', 'rosterHashMap', courseId] as const;

export function useRosterHashMap(
  courseId: string | null | undefined,
): UseQueryResult<RosterHashMapResult, ApiError> {
  const { getIdToken, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const serviceParams = useMemo(() => {
    const instructorUserId = getKuduUserId(user);

    if (!courseId || !user || !instructorUserId) {
      return null;
    }

    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    const apiClient = createApiClient({
      baseUrl: import.meta.env.VITE_API_BASE_URL || '',
      getIdToken,
      getLegacyToken: async () => createLegacyKuduToken(user),
      onAuthError: () =>
        navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
          replace: true,
        }),
    });

    return {
      apiClient,
      courseId,
      instructorUserId,
    };
  }, [courseId, getIdToken, location.hash, location.pathname, location.search, navigate, user]);

  return useQuery<RosterHashMapResult, ApiError>({
    enabled: Boolean(serviceParams),
    queryFn: async () => {
      if (!serviceParams) {
        const error = new Error('Roster hash map service is unavailable.') as ApiError;
        error.kind = 'data';
        throw error;
      }

      return fetchRosterHashMap(serviceParams);
    },
    queryKey: rosterHashMapQueryKey(courseId ?? 'missing-course'),
    staleTime: 60_000,
  } satisfies UseQueryOptions<RosterHashMapResult, ApiError>);
}
